import styled from 'styled-components';
import { ChevronUp, ChevronDown } from 'components/icons';
import * as React from 'react';

type WrapperProps = {
  isOpen: boolean;
  shiftLeft?: boolean;
};

const InfoWrapper = styled('article')<WrapperProps>`
  border-bottom: 1px solid ${(props) => (props.isOpen ? props.theme.palette.neutral.shade3 : 'transparent')};
  max-height: ${(props) => (props.isOpen ? '4000' : '32')}px;
  overflow: hidden;
  transition: max-height 0.35s ease-in-out;
  margin-bottom: 16px;
  margin-top: ${(props) => (props.shiftLeft ? '-28px' : '-10px')};
`;

const InfoTitle = styled.h4<{ shiftLeft: boolean }>`
  align-items: center;
  color: ${(props) => props.theme.palette.primary.shade4};

  &:hover,
  &:focus {
    color: ${(props) => props.theme.palette.primary.shade8};
  }

  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 500;
  justify-content: ${(props) => (props.shiftLeft ? 'flex-start' : 'flex-end')};
  gap: 8px;
  line-height: 14px;
  margin-top: 0px;
  padding: 0 0 16px 0;
  position: relative;
  user-select: none;
  transition: color 0.2s ease-in-out;

  span {
    fill: ${(props) => props.theme.palette.primary.shade4};
    width: 20px;
    height: 20px;
    align-items: center;
  }
`;

const InfoContent = styled.div`
  max-width: 100%;
  margin: 16px auto;
  text-align: left;
`;

const InfoText = styled.p`
  margin: 0 0 16px 0;
  max-width: 1000px;

  .red {
    color: red;
  }

  .amber {
    color: #dcb107;
  }

  .green {
    color: green;
  }

  .white {
    background-color: black;
    color: white;
    display: inline;
  }

  b {
    font-weight: 600;
  }

  li {
    padding-left: 16px;
    margin-bottom: 16px;
  }

  ol,
  ul {
    margin: 32px 0;
    padding-left: 32px;
  }

  a {
    text-decoration: underline;
    color: ${(props) => props.theme.palette.primary.shade4};
  }

  table {
    border-collapse: collapse;
    margin-bottom: 16px;
    width: 100%;
    border: 1px solid ${(props) => props.theme.palette.neutral.shade3};

    tr {
      border-bottom: 1px solid ${(props) => props.theme.palette.neutral.shade3};

      td {
        padding: 8px 16px;
      }

      td:first-child {
        font-weight: 600;
        padding-right: 16px;
        width: 200px;
      }
    }
  }
`;

type Props = {
  active: boolean;
  info: any;
  variant?: 'left' | 'right';
};

const InfoDetail: React.FunctionComponent<Props> = ({ active, info, variant = 'right' }) => {
  const { content, title } = info;
  const [isOpen, toggleOpen] = React.useState(active || false);
  const handleToggle = React.useCallback(() => {
    toggleOpen(!isOpen);
  }, [toggleOpen, isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleToggle();
    }
  };

  return (
    <InfoWrapper isOpen={isOpen} shiftLeft={variant === 'left'}>
      <InfoTitle onClick={handleToggle} onKeyDown={handleKeyDown} tabIndex={0} shiftLeft={variant === 'left'}>
        {title}
        {isOpen ? <ChevronUp /> : <ChevronDown />}
      </InfoTitle>
      <InfoContent>
        <InfoText dangerouslySetInnerHTML={{ __html: content }} />
      </InfoContent>
    </InfoWrapper>
  );
};

export { InfoDetail };
